import {
    makeStyles,
    Menu,
    MenuButtonProps,
    MenuGroup,
    MenuGroupHeader,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    mergeClasses,
    SplitButton,
    tokens,
} from '@fluentui/react-components';
import { useBoolean } from '@fluentui/react-hooks';
import { ChevronDown12Regular, Dismiss12Regular, History20Regular } from '@fluentui/react-icons';
import { EmitType } from '@syncfusion/ej2-base';
import { ColumnModel, GridComponent, IIndex, RowSelectEventArgs } from '@syncfusion/ej2-react-grids';
import { FC, Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetMetadataForReport } from '../../../api/collaboration/collaboration';
import { useIsFeatureEnabled } from '../../../api/features/features';
import {
    useGetComparedWhitePaperReport,
    useGetTaxReturnById,
    useGetWhitePaperReport,
    useGetWhitePaperReportPreviousVersions,
} from '../../../api/report/report';
import { useFormView } from '../../../contexts/useFormView';
import { IFilterItem } from '../../../data-types/filterItem';
import { ICurrentCellEventArgs } from '../../../data-types/ICurrentCellEventArgs';
import { IRowInfo } from '../../../data-types/IRowInfo';
import { ISelectableGrid } from '../../../data-types/ISelectableGrid';
import { createSelectedWhitePaperCell } from '../../../data-types/selectedWhitePaperCell';
import { useDrillDown } from '../../../hooks/useDrillDown';
import { useFunctionalAccess } from '../../../hooks/useFunctionalAccess';
import { useGridScroll } from '../../../hooks/useGridScroll';
import { useGridState } from '../../../hooks/useGridState';
import { useIsCollaborationUIEnabled } from '../../../hooks/useIsCollaborationUIEnabled';
import { usePerformanceMetricsForTable } from '../../../hooks/usePerformanceMetricsForTable';
import { useReportFieldCommentsDataSource } from '../../../hooks/useReportFieldCommentsDataSource';
import { useReportFieldCommentsMetadata } from '../../../hooks/useReportFieldCommentsMetadata';
import { useSearchTable } from '../../../hooks/useSearchTable';
import { useSelectedEntities } from '../../../hooks/useSelectedEntities';
import { useTaxReturnCalculationState } from '../../../hooks/useTaxReturnCalculationState';
import { useTaxReturnEventHandler } from '../../../hooks/useTaxReturnEventHandler';
import { useToastMessage } from '../../../hooks/useToastMessage';
import { useVarianceNavigation } from '../../../hooks/useVarianceNavigation';
import useVarianceThreshold from '../../../hooks/useVarianceThreshold';
import { useWhitePaperReportCache } from '../../../hooks/useWhitePaperReportCache';
import {
    IWhitePaperColumn,
    IWhitePaperMappedRowDictionary,
    IWhitePaperReport,
    IWhitePaperReportChanges,
    IWhitePaperRow,
    ProblemDetails,
    Report,
    WhitePaperReportVersion,
} from '../../../model';

import { isResponseArrayValid } from '../../../utils/ApiClientUtils';
import { generateColumns, getColumnHeaderTemplate } from '../../../utils/ColumnUtils';
import { useDataManager } from '../../../utils/DataManagerUtils';
import { getDateStringAsLocaleString } from '../../../utils/DateUtils';
import { isDevelopment } from '../../../utils/EnvironmentUtils';
import { AutoPostAmountsEnabled, FilteredEnterAmountsEnabled } from '../../../utils/FeatureFlags.constants';
import { getEntitiesFilterForColumns, getSectionsFilterItem } from '../../../utils/FilterUtils';
import {
    getDynamicFieldNameParts,
    getReportVersionLabelText,
    getVisibleEntityColumnNames,
    isDrillDownContextMenuAllowed,
    isInputRow,
    isMultiColumnReport,
} from '../../../utils/ReportUtils';
import { ContextMenuItemDisplayState, ISyncfusionContextMenuItemProps } from '../../../utils/SyncfusionContextMenuUtils';
import {
    compareValueFieldNameSuffix,
    currentValueFieldNameSuffix,
    TOTAL_ROW_NUMBER,
    varianceValueFieldNameSuffix,
} from '../../../utils/Table.constants';
import {
    clickOnVarianceContainedByCell,
    getColumnNameFromCurrentComparedColumnName,
    setFocusOnSyncfusionGridCell,
} from '../../../utils/TableUtils';
import { getFilteredTOCItemsByCondition } from '../../../utils/TaxReturnTOCUtils';
import DefaultSeparator from '../../common/DefaultSeparator';
import GridSearchBar from '../../common/GridSearchBar';
import Modal from '../../common/ModalV9';
import EnterAmountsDialog, { IEnterAmountsDialogProps } from '../../enter-amounts-dialog/EnterAmountsDialog';
import FilterButton from '../../filters/filter-button/FilterButton';
import { useFilterPickerStyles } from '../../filters/filter-picker/FilterPicker';
import { CommentsPanelWrapper } from '../../sidebar/CommentsPanelWrapper';
import ReportDrillDown, { IReportDrillDownOnClose } from '../../sidebar/ReportDrillDown';
import ActionBarButtons from '../action-buttons/actionBarButtons';
import CalculateButton from '../action-buttons/calculateButton';
import CommonIndividualReportTable, {
    descriptionTemplate,
    ICommonIndividualReportTableContextMenuProps,
} from '../common-individual-report-table/CommonIndividualReportTable';
import ScrollToSectionDropdown from '../common-individual-report-table/ScrollToSectionDropdown';
import VarianceGoToNavigation from '../common-individual-report-table/VarianceGoToNavigation';
import VarianceThresholdButton from '../common-individual-report-table/VarianceThresholdButton';

export interface ICompareReportsTableProps {
    navigateToReport: (reportId: number) => void;
    navigateToCompareReport: (versionId: string, selectedEntities: string[]) => void;
    reportId: number;
    versionId: string;
    errorModalOnClose: () => void;
}
const useHistoryMenuStyles = makeStyles({
    popover: {
        maxWidth: '350px',
        width: '350px',
        minWidth: '350px',
    },
    item: {
        display: 'flex',
        alignItems: 'center',
        minHeight: '36px',
        fontSize: '13px',
        fontWeight: '600',
        paddingLeft: '24px',
        paddingRight: '24px',
        maxWidth: '100%',
        width: '100%',
    },
    activeItem: {
        color: tokens.colorBrandForeground1,
    },
    primaryMenuChevron: {
        display: 'inline-flex',
        marginLeft: 'auto',
    },
});

const CompareReportsTable: FC<ICompareReportsTableProps> = (props: ICompareReportsTableProps) => {
    const { trackScrollStarted, trackScrollFinished, trackRenderFinished } = usePerformanceMetricsForTable('Compare return');
    const clickableColumnStartIndex: number = 2;
    const { t } = useTranslation();
    const { showToast } = useToastMessage();
    const { data: isAutoPostAmountsEnabled } = useIsFeatureEnabled({ featureFlagName: AutoPostAmountsEnabled });
    const { data: isFilteredEnterAmountsEnabled } = useIsFeatureEnabled({ featureFlagName: FilteredEnterAmountsEnabled });
    const { setTraceCalculationData } = useDrillDown();
    const selectableGridRef = useRef<ISelectableGrid | null>(null);
    const gridComponentRef = useRef<GridComponent | null>(null);
    const { selectedEntities, setSelectedEntitiesSearchParams, getFilteredEntities } = useSelectedEntities();
    const {
        varianceThresholdCookieValue,
        varianceThresholdTypeCookieValue: varianceThresholdType,
        varianceThresholdDisplayFormatCookieValue: varianceThresholdDisplayFormat,
        showOnlyExceedingRowsCookieValue,
    } = useVarianceThreshold(props.reportId.toString());
    const isValidThresholdCookie = varianceThresholdCookieValue !== undefined && varianceThresholdCookieValue >= 0;

    const {
        isReportExecuting: isInTaxCalculationReportList,
        isReportPrinting,
        rerunCalculationReport,
        registerDependency,
    } = useTaxReturnCalculationState();
    const { setHasContextChangedFalse, hasContextChanged, setNewWarningMessage, clearWarningMessage, warningMessage } =
        useTaxReturnEventHandler();
    const [currentCellArgs, setCurrentCellArgs] = useState<ICurrentCellEventArgs<IWhitePaperRow> | null>(null);
    const {
        data: currentWhitePaperReport,
        isError: isComparedWhitePaperReportError,
        isFetching: isFetchingComparedReport,
        queryKey: queryKeyReport,
        error: comparedReportError,
    } = useGetComparedWhitePaperReport<IWhitePaperReport>({
        ReportId: props.reportId,
        VersionId: props.versionId,
        VarianceThreshold: isValidThresholdCookie ? varianceThresholdCookieValue : null,
        VarianceThresholdType: varianceThresholdType,
        VarianceThresholdDisplayFormat: varianceThresholdDisplayFormat,
        SelectedEntities: selectedEntities,
    });

    const {
        data: currentReportInformation,
        isError: isTaxReturnError,
        isFetching: isFetchingTaxReturn,
    } = useGetTaxReturnById<Report>(props.reportId);

    const {
        data: report,
        isError: isErrorReportMap,
        isFetching: isFetchingReportMap,
    } = useGetWhitePaperReport<IWhitePaperReport>({ ReportId: props.reportId });

    const { data: availableVersions, queryKey: previousVersionsKey } = useGetWhitePaperReportPreviousVersions({
        ReportId: props.reportId,
    });

    // Cache-related variables for updating cached reports when amounts change
    const { updateCachedReportWithChanges, latestCalculatedVersionId } = useWhitePaperReportCache(props.reportId);

    const { data: currentReportMetadata, refetch: refetchReportMetadata } = useGetMetadataForReport(props.reportId.toString());

    const { dataManager, contentLength } = useDataManager(
        currentWhitePaperReport!,
        props.reportId.toString(),
        showOnlyExceedingRowsCookieValue,
        currentReportMetadata?.metadata
    );
    const isMultiColumns = isMultiColumnReport(currentWhitePaperReport);
    const [currentWhitePaperColumn, setCurrentWhitePaperColumn] = useState<IWhitePaperColumn | undefined>(undefined);
    const [isDrillDownVisible, { setTrue: showDrillDown, setFalse: hideDrillDown }] = useBoolean(false);
    const [isCommentsPanelOpen, setIsCommentsPanelOpen] = useState(false);
    const [commentData, setCommentData] = useState<{ rowData: IWhitePaperRow; columnName: string } | null | undefined>(undefined);
    const [changeTrackingData, setChangeTrackingData] = useState<IWhitePaperReportChanges>({});

    useEffect(() => {
        registerDependency(props.reportId, queryKeyReport);
    }, [props.reportId]);

    const entities: IFilterItem[] = getEntitiesFilterForColumns(currentWhitePaperReport?.columns || []);
    const filteredEntities: IFilterItem[] = getFilteredEntities(entities);
    const { openForm, showForm, tocData, hasTaxReturnTOCItems, isLoadingToc, setCurrentWhitePaperReport, setCurrentReport } = useFormView();

    const getColumns = useCallback(() => {
        const rowNumberColumnModel: ColumnModel = {
            field: 'rowNumber',
            width: '100px',
            headerText: 'Row Number',
            isFrozen: isMultiColumns,
            freeze: isMultiColumns ? 'Left' : 'None',
            allowResizing: true,
            autoFit: false,
            allowEditing: false,
            isPrimaryKey: true,
            visible: isDevelopment(),
        };
        const descriptionColumnModel: ColumnModel = {
            field: 'description',
            maxWidth: '3130px',
            width: '450px',
            headerText: t('description').toString(),
            isFrozen: isMultiColumns,
            freeze: isMultiColumns ? 'Left' : 'None',
            headerTemplate: getColumnHeaderTemplate,
            template: descriptionTemplate,
            customAttributes: { class: 'white-paper-report-customizable-cell' },
            allowEditing: false,
            visible: true,
            editTemplate: (props: IWhitePaperRow) => <span style={{ paddingLeft: '23px' }}>{props.description}</span>,
        };

        return [
            rowNumberColumnModel,
            descriptionColumnModel,
            ...generateColumns(
                currentWhitePaperReport,
                t,
                filteredEntities,
                currentValueFieldNameSuffix,
                compareValueFieldNameSuffix,
                varianceValueFieldNameSuffix,
                getDateStringAsLocaleString
            ),
        ];
    }, [currentWhitePaperReport, t, filteredEntities, getColumnHeaderTemplate, descriptionTemplate, isMultiColumns]);

    const getColumnIdFromFieldName = useCallback((fieldName: string): string => {
        if (!fieldName.includes('.')) {
            return fieldName;
        }

        const { entityCode } = getDynamicFieldNameParts(fieldName);
        return entityCode;
    }, []);

    const isMenuEnabledForTableColumn = (cellIndex: number): boolean => {
        return cellIndex >= clickableColumnStartIndex;
    };

    const isDrillDownAllowed = (cellIndex?: IIndex) => {
        if (cellIndex && cellIndex.cellIndex) {
            return isMenuEnabledForTableColumn(cellIndex.cellIndex);
        }
        return false;
    };
    const [enterAmountsDialogProps, setEnterAmountsDialogProps] = useState<IEnterAmountsDialogProps | null>(null);
    const openEnterAmountsIfAllowed = (eventArgs?: ICurrentCellEventArgs<IWhitePaperRow> | null) => {
        if (eventArgs) {
            const currentColumnName = getColumnNameFromCurrentComparedColumnName(eventArgs);
            const currentColumn = currentWhitePaperReport?.columns?.find((column) => column.fieldName === currentColumnName);
            const isInput = isInputRow(eventArgs.data);
            const rowDescription =
                isInput && isFilteredEnterAmountsEnabled
                    ? `Row ${eventArgs.data.description}, ${currentColumn?.entityCode} ${currentColumn?.entityName} `
                    : '';
            setEnterAmountsDialogProps({
                //buttonTrigger: eventArgs as HTMLElement,
                rowDescription: rowDescription,
                columnId: currentColumn?.originalColumnId,
                drillDownRowNumber: Number.parseInt(eventArgs.data.displayRowNumber!),
                onClose: () => setEnterAmountsDialogProps(null),
                entity: currentColumn?.entityCode ?? currentReportInformation!.entityCode!,
                isInputRow: isInput,
            } as unknown as IEnterAmountsDialogProps);
        }
    };
    const openDrillDownIfAllowed = (eventArgs?: ICurrentCellEventArgs<IWhitePaperRow> | null) => {
        if (eventArgs) {
            const currentColumnName = getColumnNameFromCurrentComparedColumnName(eventArgs);
            if (currentColumnName) {
                const currentColumn = currentWhitePaperReport?.columns?.find((column) => column.fieldName === currentColumnName);
                if (currentColumn) {
                    setCurrentWhitePaperColumn(currentColumn);
                    setTraceCalculationData([
                        { displayRowNumber: TOTAL_ROW_NUMBER },
                        createSelectedWhitePaperCell(eventArgs.data, currentColumnName),
                    ]);
                    showDrillDown();
                }
            }
        }
    };

    const closeDrillDown = (changeTrackingData: IReportDrillDownOnClose) => {
        const { amountsChangedOnServer, reportChanges } = changeTrackingData;
        hideDrillDown();
        setNewWarningMessage(amountsChangedOnServer, t('recalculateNeeded').toString());
        setChangeTrackingData(reportChanges);
        if (selectableGridRef.current) {
            selectableGridRef.current.focusCell(undefined as unknown as IIndex);
            if (amountsChangedOnServer) {
                selectableGridRef.current.markAsEdited(currentCellArgs?.cellIndex!);
            }
        }
    };

    const openCommentsIfAllowed = (eventArgs?: ICurrentCellEventArgs<IWhitePaperRow> | null) => {
        if (eventArgs) {
            if (isDrillDownAllowed(eventArgs.cellIndex)) {
                setCommentData({ rowData: eventArgs.data, columnName: getColumnIdFromFieldName(eventArgs.fieldName) });
                setIsCommentsPanelOpen(true);
            }
        }
    };

    useEffect(() => {
        setNewWarningMessage(currentWhitePaperReport?.reCalculationRequired ?? false, t('recalculateNeeded').toString());
        setChangeTrackingData(currentWhitePaperReport?.changeTrackingData ?? {});
        currentWhitePaperReport && setCurrentWhitePaperReport(currentWhitePaperReport);
    }, [currentWhitePaperReport]);

    useEffect(() => {
        registerDependency(props.reportId, previousVersionsKey);
    }, [previousVersionsKey]);

    useEffect(() => {
        setNewWarningMessage(hasContextChanged, t('calculationContextChanged').toString());
    }, [hasContextChanged]);

    useEffect(() => {
        if (currentReportInformation) {
            setCurrentReport(currentReportInformation);
        }
    }, [currentReportInformation, props.reportId]);

    const isFetching = isFetchingTaxReturn || isFetchingComparedReport || isFetchingReportMap;
    const currentReportIsCalculating = isInTaxCalculationReportList(props.reportId) || false;

    const { isSearchValueApplied, clearSearch } = useSearchTable();

    const { grid, isFilterApplied } = useGridState();

    const {
        setIsVarianceNavigating,
        setCurrentVarianceIndex,
        horizontallyScrollToVariance,
        isDisabledVarianceGoToNavigation,
        setIsDisabledVarianceGoToNavigation,
    } = useVarianceNavigation();

    const navigateToReport = () => {
        setCurrentVarianceIndex(0);
        clearSearch(grid);
        props.navigateToReport(props.reportId);
    };
    const handleChangeCompareDate = () => {
        clearSearch(grid);
        setCurrentVarianceIndex(0);
        setIsDisabledVarianceGoToNavigation(false);
    };

    const getCurrentCompareReportName = (): string =>
        availableVersions?.find((version) => version.versionId === (props.versionId ?? ''))
            ? getReportVersionLabelText(availableVersions.find((version) => version.versionId === (props.versionId ?? ''))!)
            : '';

    const rerunCalculation = async () => {
        if (isAutoPostAmountsEnabled && currentReportInformation?.updateBackReport && !functionalAccess?.postAmountEnabled) {
            showToast(
                t('calculationInProgress').toString(),
                <span>{t('amountsNotPostedDueToInsufficientPermissions').toString()}</span>,
                'info'
            );
        }
        rerunCalculationReport(
            currentReportInformation?.name,
            props.reportId,
            currentReportInformation?.taxReturnItemId ?? '',
            undefined,
            (_data: any) => {
                clearWarningMessage();
                setHasContextChangedFalse();
            }
        );
    };

    const compareHistoryButtonId = `compareHistoryButton`;
    const [isCompareHistoryMenuOpen, setIsCompareHistoryMenuOpen] = useState(false);
    const {
        popover: historyMenuPopover,
        item: historyMenuItem,
        activeItem: activeHistoryMenuItem,
        primaryMenuChevron,
    } = useHistoryMenuStyles();
    const filterPickerStyles = useFilterPickerStyles();

    const reportVersionsMenuList = (
        <MenuList aria-label={t('compareHistory').toString()}>
            <MenuGroup>
                <MenuGroupHeader>{t('datesRun').toString()}</MenuGroupHeader>
                {isResponseArrayValid(availableVersions) &&
                    availableVersions?.map((version) => (
                        <MenuItem
                            key={version.versionId}
                            className={mergeClasses(historyMenuItem, version.versionId === props.versionId && activeHistoryMenuItem)}
                            data-analytics-button-name='compare_reports_menu_item'
                            onClick={() => {
                                props.navigateToCompareReport(version.versionId ?? '', selectedEntities);
                                handleChangeCompareDate();
                            }}
                        >
                            {getReportVersionLabelText(version)}
                        </MenuItem>
                    ))}
            </MenuGroup>
        </MenuList>
    );

    const selectedVersion = availableVersions
        ? availableVersions.find((version) => version.versionId === (props.versionId ?? ''))
        : undefined;
    const getTitleForReport = (reportInformation: Report | undefined, version: WhitePaperReportVersion | undefined): string => {
        if (version && version.dateTime) {
            return `${t('titleCompare')} ${reportInformation?.name} ${t('titleCompareTo')} ${getDateStringAsLocaleString(
                version.dateTime
            )}`;
        }

        return reportInformation?.name || t('taxReturns');
    };
    document.title = getTitleForReport(currentReportInformation, selectedVersion);

    const getCompareButton: () => JSX.Element = () => {
        if (!availableVersions?.length) {
            return <Fragment key='compareButtonWrapper'></Fragment>;
        }

        const compareReportName = getCurrentCompareReportName();
        const closeCompareLabel = t('closeCompareMode').toString();
        const isCompareControlsDisabled = isFetching || currentReportIsCalculating;

        return (
            <Fragment key='compareButtonWrapper'>
                <DefaultSeparator />
                <Menu open={isCompareHistoryMenuOpen} onOpenChange={(_ev, data) => setIsCompareHistoryMenuOpen(data.open)}>
                    <MenuTrigger disableButtonEnhancement>
                        {(triggerProps: MenuButtonProps) => (
                            <SplitButton
                                icon={<History20Regular />}
                                menuIcon={<Dismiss12Regular />}
                                primaryActionButton={{
                                    ...triggerProps,
                                    id: compareHistoryButtonId,
                                    className: mergeClasses(
                                        filterPickerStyles.activeTrigger,
                                        isCompareHistoryMenuOpen && filterPickerStyles.openTrigger,
                                        triggerProps.className
                                    ),
                                    'aria-label': compareReportName,
                                    'data-analytics-button-name': 'compare_history_button',
                                    disabled: isCompareControlsDisabled,
                                }}
                                menuButton={{
                                    className: mergeClasses(
                                        filterPickerStyles.activeTrigger,
                                        isCompareHistoryMenuOpen && filterPickerStyles.openTrigger
                                    ),
                                    'aria-label': closeCompareLabel,
                                    'data-testid': 'icon-button-cancel',
                                    onClick: (ev) => {
                                        ev.stopPropagation();
                                        ev.preventDefault();
                                        navigateToReport();
                                    },
                                }}
                            >
                                {compareReportName}
                                <ChevronDown12Regular className={primaryMenuChevron} />
                            </SplitButton>
                        )}
                    </MenuTrigger>
                    <MenuPopover className={historyMenuPopover}>{reportVersionsMenuList}</MenuPopover>
                </Menu>
                <VarianceThresholdButton disabled={isFetching || isFilterApplied || currentReportIsCalculating} reportId={props.reportId} />
                <VarianceGoToNavigation
                    variancePoints={currentWhitePaperReport?.variances || []}
                    isDisabled={isFetching || isDisabledVarianceGoToNavigation || isSearchValueApplied || currentReportIsCalculating}
                    showOnlyExceedingRows={showOnlyExceedingRowsCookieValue}
                />
            </Fragment>
        );
    };

    const compareButton: JSX.Element = getCompareButton();
    const { currentColumnIndex, currentRowIndex, programmaticallyScrollToRow } = useGridScroll();
    const onRowSelected: EmitType<RowSelectEventArgs> = () => {
        if (currentColumnIndex.current) {
            if (isMultiColumns) {
                horizontallyScrollToVariance(grid.current, currentColumnIndex.current);
            }

            clickOnVarianceContainedByCell(grid.current, {
                rowIndex: currentRowIndex.current ?? undefined,
                cellIndex: currentColumnIndex.current ?? undefined,
            });
        }
        setIsVarianceNavigating(false);
    };

    const sections = useMemo(() => {
        const statements = getFilteredTOCItemsByCondition(tocData?.items ?? [], (item) => item.isStatement ?? false);

        return getSectionsFilterItem(currentWhitePaperReport?.rows || [], statements || []);
    }, [currentWhitePaperReport, tocData]);

    const onSectionsScrollToChange = (filteredItems: IFilterItem[]) => {
        if (grid && grid.current) {
            const rowId = parseInt(filteredItems[0].key, 10);
            const rowIndex = currentWhitePaperReport?.rows?.findIndex((row) => row.rowNumber === rowId);
            programmaticallyScrollToRow(grid.current, { rowIndex: rowIndex });
        }
    };
    const [searchEntityValue, setSearchEntityValue] = useState<string>('');
    const updateVisibleColumns = (filteredItems: IFilterItem[]) => {
        if (grid && grid.current && currentWhitePaperReport) {
            getVisibleEntityColumnNames(filteredItems, currentWhitePaperReport?.columns ?? []);
        }
    };
    const onFilterChange = (filteredItems: IFilterItem[]) => {
        setCurrentVarianceIndex(0);
        setSelectedEntitiesSearchParams(filteredItems);
        updateVisibleColumns(filteredItems);
    };
    const handlerSetCheckedItems = (filterItems: IFilterItem[]) => {
        onFilterChange(filterItems);
        clearSearch(gridComponentRef);
    };

    const columns = getColumns();

    const getTablePerformanceInformation = () => ({
        rowCount: contentLength,
        columnCount: columns.length,
        visibleColumnCount: columns.filter((column) => column.visible).length,
        reportId: props.reportId,
        isMultiColumn: isMultiColumns,
    });

    const renderFinished = () => {
        trackRenderFinished(getTablePerformanceInformation());
    };

    const onGridDataBound = () => {
        trackScrollFinished(getTablePerformanceInformation());
    };

    const calculateButton = CalculateButton({
        onClick: rerunCalculation,
        disabled: isFetching || currentReportIsCalculating || isReportPrinting(props.reportId) || showForm,
    });

    const filterGroupItems: React.ReactNode[] = [
        <ScrollToSectionDropdown
            sections={sections}
            label='goToSection'
            onSectionChange={onSectionsScrollToChange}
            isDisabled={showOnlyExceedingRowsCookieValue}
            key='scrollToSectionDropdown'
        />,
        isMultiColumns ? (
            <FilterButton
                data-testid='filterButton'
                filterName='entityName'
                items={entities}
                label='entity'
                checkedItems={filteredEntities}
                setCheckedItems={handlerSetCheckedItems}
                searchValue={searchEntityValue}
                setSearchValue={setSearchEntityValue}
                noElementsFoundtext={t('noEntitiesFound')}
                key='filterEntityButton'
            />
        ) : null,
        <GridSearchBar searchBoxPlaceholderText={t('search')} key='gridSearchBar' />,
        compareButton,
    ];

    const modalBodyContent = (problemDetails: ProblemDetails) => (
        <>
            <p>{t('compareReportsErrorModalBody')}</p>
            <p>{`${t('compareReportsErrorModalCorrelationIdLabel')} ${problemDetails?.traceId}`}</p>
        </>
    );

    const isFormButtonLoading = isFetching || !currentWhitePaperReport || isLoadingToc;

    const actionBarButtons = ActionBarButtons({
        isReportActive: !showForm,
        handleOpenPathToPortal: openForm,
        navigateToReport: () => props.navigateToCompareReport(props.versionId ?? '', selectedEntities),
        hasForms: hasTaxReturnTOCItems,
        // Compare history is only reachable for a return that already has a calculated report to compare against.
        hasReport: true,
        isLoading: isFormButtonLoading,
    });

    const onEnterAmountsClosedCallback: (amountsChangedOnServer: boolean, updatedChangeTrackingData: IWhitePaperReportChanges) => void = (
        amountsChangedOnServer: boolean,
        updatedChangeTrackingData: IWhitePaperReportChanges
    ) => {
        setEnterAmountsDialogProps(null);
        setNewWarningMessage(amountsChangedOnServer, t('recalculateNeeded').toString());
        setChangeTrackingData(updatedChangeTrackingData);
        if (amountsChangedOnServer && currentWhitePaperReport) {
            updateCachedReportWithChanges(
                currentWhitePaperReport,
                updatedChangeTrackingData,
                amountsChangedOnServer,
                props.reportId,
                latestCalculatedVersionId
            );
        }
        if (selectableGridRef.current) {
            selectableGridRef.current.focusCell(undefined as unknown as IIndex);
            if (changeTrackingData !== updatedChangeTrackingData) {
                selectableGridRef.current.markAsEdited(currentCellArgs?.cellIndex!);
                gridComponentRef?.current?.refresh();
                selectableGridRef.current.refreshGrid();
            }
        }
    };

    const handleCommentsPanelClose = () => {
        handleRefetchMetadata();
        const item = document.querySelector('.item-last-focused') as HTMLElement;
        setFocusOnSyncfusionGridCell(item);
        setIsCommentsPanelOpen(false);
    };

    const handleRefetchMetadata = () => {
        refetchReportMetadata();
    };

    // Create data source and metadata for field-level comments using new hooks
    const fieldDataSource = useReportFieldCommentsDataSource(
        props.reportId.toString(),
        commentData?.rowData?.rowNumber?.toString() ?? '',
        encodeURIComponent(commentData?.columnName ?? ''),
        currentReportInformation?.name ?? undefined
    );
    const fieldMetadata = useReportFieldCommentsMetadata(
        props.reportId.toString(),
        currentReportInformation?.name ?? undefined,
        commentData?.rowData?.rowNumber,
        encodeURIComponent(commentData?.columnName ?? ''),
        commentData?.rowData?.description ?? undefined,
        undefined // reportColumnTitle not available in this context
    );

    const { functionalAccess } = useFunctionalAccess();
    const isCollaborationUIEnabled = useIsCollaborationUIEnabled();

    /* in order to have the grid reflect updates to the metadata after it is data bound, we have to invoke refresh */
    useEffect(() => {
        if (selectableGridRef.current) {
            selectableGridRef.current.refreshGrid();
        }
    }, [currentReportMetadata]);

    return (
        <>
            {isCollaborationUIEnabled && (
                <CommentsPanelWrapper
                    dataSource={fieldDataSource}
                    metadata={fieldMetadata}
                    isOpen={isCommentsPanelOpen}
                    onClose={handleCommentsPanelClose}
                    isCollaborationEnabled={isCollaborationUIEnabled}
                />
            )}
            <CommonIndividualReportTable
                key={props.reportId}
                taxReturnId={currentReportInformation?.taxReturnItemId}
                columns={columns}
                currentReport={currentWhitePaperReport}
                isError={isTaxReturnError || isComparedWhitePaperReportError || isErrorReportMap}
                isTableDataReadyToDisplay={!isFetching}
                componentRef={selectableGridRef}
                dataManager={dataManager}
                currentCellArgs={currentCellArgs}
                setCurrentCellArgs={setCurrentCellArgs}
                onRenderFinished={renderFinished}
                onScrollingStarted={trackScrollStarted}
                onGridDataBound={onGridDataBound}
                handleRefetchMetadata={handleRefetchMetadata}
                contextMenuProps={
                    {
                        enterAmounts: {
                            getVisibilityState: (rowData: IRowInfo<IWhitePaperRow>) => {
                                if (isDrillDownContextMenuAllowed(rowData, isMultiColumns, currentWhitePaperReport, isDrillDownAllowed)) {
                                    if (functionalAccess?.amountDataEntryAccessEnabled) {
                                        return ContextMenuItemDisplayState.Enabled;
                                    }
                                    return ContextMenuItemDisplayState.Disabled;
                                }
                                return ContextMenuItemDisplayState.Invisible;
                            },
                            onMenuItemClick: openEnterAmountsIfAllowed,
                        } as ISyncfusionContextMenuItemProps<IWhitePaperRow>,
                        drillDown: {
                            getVisibilityState: (rowData: IRowInfo<IWhitePaperRow>) => {
                                if (isDrillDownContextMenuAllowed(rowData, isMultiColumns, currentWhitePaperReport, isDrillDownAllowed)) {
                                    if (functionalAccess?.amountDataEntryAccessEnabled) {
                                        return ContextMenuItemDisplayState.Enabled;
                                    }
                                    return ContextMenuItemDisplayState.Disabled;
                                }
                                return ContextMenuItemDisplayState.Invisible;
                            },
                            onMenuItemClick: openDrillDownIfAllowed,
                        } as ISyncfusionContextMenuItemProps<IWhitePaperRow>,
                        comments: {
                            getVisibilityState: (rowData: IRowInfo<IWhitePaperRow>) => {
                                const cellIndex = { ...rowData } as IIndex;
                                if (isCollaborationUIEnabled && isDrillDownAllowed(cellIndex)) {
                                    return ContextMenuItemDisplayState.Enabled;
                                }
                                return ContextMenuItemDisplayState.Invisible;
                            },
                            onMenuItemClick: openCommentsIfAllowed,
                        } as ISyncfusionContextMenuItemProps<IWhitePaperRow>,
                        editCell: {
                            getVisibilityState: () => ContextMenuItemDisplayState.Invisible,
                        } as ISyncfusionContextMenuItemProps<IWhitePaperRow>,
                    } as ICommonIndividualReportTableContextMenuProps
                }
                reportId={props.reportId}
                versionId={props.versionId}
                calculateButton={calculateButton}
                actionBar={actionBarButtons}
                filterGroupItems={filterGroupItems}
                onRowSelected={onRowSelected}
                clearAllFilters={() => handlerSetCheckedItems([])}
                shouldDisplayCalculationOptions={false}
                loadingIndicator={{ indicatorType: 'Shimmer' }}
                headerWarningMessage={warningMessage}
                isCollaborationEnabled={isCollaborationUIEnabled}
            />

            {isDrillDownVisible && (
                <ReportDrillDown
                    onClose={closeDrillDown}
                    report={currentReportInformation!}
                    showPanel={isDrillDownVisible}
                    whitePaperReportMap={report?.rowsMap as IWhitePaperMappedRowDictionary}
                    whitePaperReportColumn={currentWhitePaperColumn}
                    changeTrackingData={changeTrackingData}
                    isMultiColumnWhitePaperReport={isMultiColumns}
                />
            )}
            {enterAmountsDialogProps && (
                <EnterAmountsDialog
                    {...enterAmountsDialogProps}
                    case={currentReportInformation!.case!}
                    jurisdiction={currentReportInformation!.jurisdiction!}
                    period={currentReportInformation!.year!.toString()}
                    adjustmentCode=''
                    location={currentReportInformation!.location!}
                    changeTrackingData={changeTrackingData}
                    taxReturnKey={currentReportInformation!.id!}
                    onClose={onEnterAmountsClosedCallback}
                />
            )}

            {comparedReportError && (
                <Modal
                    isOpen={true}
                    onClose={props.errorModalOnClose}
                    headerContent={<h2>{comparedReportError.response?.data.title}</h2>}
                    bodyContent={modalBodyContent(comparedReportError?.response?.data as ProblemDetails)}
                />
            )}
        </>
    );
};

export { CompareReportsTable as default };
